import React, {useState} from 'react';
import moment from 'moment';
import Env from '../../../../../../util/env';
import Resources from '../../../../../../data/services/resources';
import {getJWT} from '../../../../../util/util-auth';
import {classNames, getAvatarColorClass} from '../../../../../util/util-helpers';
import FieldTextarea from '../../../../fields/field-textarea';

export default function CommentCard({
    item,
    isMe,
    editedComment,
    editComment,
    editCommentHandler,
    confirmDeleteCommentHandler,
    onAvatarClick,
    translate,
}) {
    const initials = [item.FirstName, item.LastName]
        .filter(Boolean)
        .map(n => n[0].toUpperCase())
        .join('');

    const avatarColor = getAvatarColorClass(initials);
    const [imgFailed, setImgFailed] = useState(false);

    const imageUrl = item.ImagePath
        ? Env.getApiUrl('api/' + Resources.ContactImage, {
            token: getJWT().access_token,
            ContactID: item.ContactID,
        })
        : null;

    const showImage = imageUrl && !imgFailed;

    const isEditing = item.TaskCommentID == editedComment?.id;
    const isDeleted = !!item.ArchivedDate;

    return (
        <div className="flex gap-3">
            <button
                type="button"
                onClick={() => onAvatarClick?.(item)}
                className={classNames(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-full overflow-hidden',
                    !showImage ? `text-xs font-semibold text-inverse ${avatarColor}` : ''
                )}
            >
                {showImage ? (
                    <img
                        src={imageUrl}
                        alt={initials}
                        className="h-full w-full object-cover"
                        onError={() => setImgFailed(true)}
                    />
                ) : initials}
            </button>

            <div className="min-w-0 flex-1">
                <p className="text-sm">
                    <span
                        onClick={() => onAvatarClick?.(item)}
                        className={classNames(
                            'font-semibold text-tm-gray-900',
                            onAvatarClick ? 'cursor-pointer hover:text-primary' : ''
                        )}
                    >
                        {item.FirstName} {item.LastName}
                    </span>

                    <span className="ml-2 text-xs text-tm-gray-400">
                        {moment(item.TaskCommentCreated).fromNow()}
                    </span>

                    {!!item.TaskCommentEdited && (
                        <span className="ml-1 text-xs text-tm-gray-400">({translate('text.edited')})</span>
                    )}
                </p>

                {!isEditing && (
                    isDeleted ? (
                        <p className="mt-1 text-sm italic text-tm-gray-400">
                            [{translate('text.message_deleted')}]
                        </p>
                    ) : (
                        <p className="mt-1 text-sm leading-relaxed text-tm-gray-700 whitespace-pre-wrap">
                            {item.TaskComment}
                        </p>
                    )
                )}

                {isEditing && (
                    <div className="mt-2">
                        <FieldTextarea
                            id="textInput"
                            onChange={(name, value) => editComment(item.TaskCommentID, value)}
                            value={editedComment.value}
                            addClass="form-control whitespace-pre-wrap"
                        />

                        <div className="mt-2 flex gap-2">
                            <button
                                type="button"
                                onClick={() => editedComment.value ? editCommentHandler() : null}
                                className={classNames('btn btn-outline', !editedComment.value ? 'disabled' : '')}
                            >
                                {translate('btn.edit_comment')}
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => editComment(null, null)}
                            >
                                {translate('btn.close')}
                            </button>
                        </div>
                    </div>
                )}

                {!isDeleted && !isEditing && (
                    <div className="mt-1.5 flex gap-3 text-xs font-medium text-tm-gray-500">
                        <button type="button" className="hover:text-tm-gray-700 hover:underline">
                            {translate('btn.reply')}
                        </button>

                        {isMe && (
                            <button
                                type="button"
                                onClick={() => editComment(item.TaskCommentID, item.TaskComment)}
                                className="hover:text-tm-gray-700 hover:underline"
                            >
                                {translate('btn.edit')}
                            </button>
                        )}

                        {isMe && (
                            <button
                                type="button"
                                onClick={() => confirmDeleteCommentHandler(item.TaskCommentID)}
                                className="hover:text-tm-gray-700 hover:underline"
                            >
                                {translate('btn.delete')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
